import React, { useState } from "react";
import {
  ModalOverlay,
  ModalContainer,
} from "../Modal/Modal.styled";
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Divider,
  Chip,
  TextField,
  Button,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TableChartIcon from "@mui/icons-material/TableChart";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { useTableColumnDescription } from "../../hooks/useTableColumnDescription";

const ColumnDescriptionModal = ({
  open,
  onClose,
  tableName,
  columnName,
  userDbId,
}) => {
  const {
    getTableColumnDescription,
    updateTableColumnDescription,
    loading,
    error,
    data: columnData,
    updating,
    updateError,
  } = useTableColumnDescription();

  const [isEditMode, setIsEditMode] = useState(false);
  const [editedDescription, setEditedDescription] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState(false);

  React.useEffect(() => {
    if (open && tableName && columnName && userDbId !== undefined && userDbId !== null) {
      getTableColumnDescription({
        userDbId,
        tableName,
        columnName,
      });
      setIsEditMode(false);
      setUpdateSuccess(false);
    }
  }, [open, tableName, columnName, userDbId, getTableColumnDescription]);

  React.useEffect(() => {
    if (columnData) {
      setEditedDescription(columnData.description || "");
    } else {
      // Initialize empty when no data exists
      setEditedDescription("");
    }
  }, [columnData]);

  const handleEdit = () => {
    setIsEditMode(true);
    setEditedDescription(columnData?.description || "");
    setUpdateSuccess(false);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditedDescription(columnData?.description || "");
    setUpdateSuccess(false);
  };

  const handleSave = async () => {
    if (!editedDescription.trim()) {
      return;
    }

    const result = await updateTableColumnDescription({
      userDbId,
      tableName,
      columnName,
      description: editedDescription,
    });

    if (result.success) {
      setUpdateSuccess(true);
      setIsEditMode(false);
      // Refetch the data to show updated description
      await getTableColumnDescription({
        userDbId,
        tableName,
        columnName,
      });
      setTimeout(() => setUpdateSuccess(false), 3000);
    }
  };

  if (!open) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer
        onClick={(e) => e.stopPropagation()}
        sx={{ 
          maxWidth: 560, 
          width: "90%",
          padding: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 24px",
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#fafafa",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#3F49A7",
              fontSize: 18,
              letterSpacing: "-0.01em",
            }}
          >
            Column Description
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "#666",
              "&:hover": { 
                backgroundColor: "rgba(63, 73, 167, 0.08)",
                color: "#3F49A7",
              },
              transition: "all 0.2s",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ padding: "24px" }}>
          {/* Table and Column Info */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flex: 1,
                }}
              >
                <TableChartIcon
                  sx={{
                    fontSize: 18,
                    color: "#3F49A7",
                    opacity: 0.8,
                  }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#666",
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    Table
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#1a1a1a",
                      fontSize: 15,
                      fontWeight: 600,
                      fontFamily: "monospace",
                    }}
                  >
                    {tableName}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flex: 1,
                }}
              >
                <ViewColumnIcon
                  sx={{
                    fontSize: 18,
                    color: "#3F49A7",
                    opacity: 0.8,
                  }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#666",
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    Column
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#1a1a1a",
                      fontSize: 15,
                      fontWeight: 600,
                      fontFamily: "monospace",
                    }}
                  >
                    {columnName}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mb: 3, borderColor: "#e0e0e0" }} />

          {/* Loading State */}
          {loading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                py: 6,
              }}
            >
              <CircularProgress 
                size={32} 
                sx={{ color: "#3F49A7", mb: 2 }} 
              />
              <Typography
                variant="body2"
                sx={{
                  color: "#666",
                  fontSize: 13,
                }}
              >
                Loading description...
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Box
              sx={{
                width: "100%",
                p: 2.5,
                backgroundColor: "#ffebee",
                borderRadius: 2,
                border: "1px solid #ffcdd2",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#c62828",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                Error loading column description
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#b71c1c",
                  fontSize: 12,
                  mt: 0.5,
                  display: "block",
                }}
              >
                {error.message}
              </Typography>
            </Box>
          )}

          {/* Content State */}
          {!loading && !error && columnData && (
            <Box>
              {/* Data Type */}
              {columnData.data_type && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#666",
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      display: "block",
                      mb: 1,
                    }}
                  >
                    Data Type
                  </Typography>
                  <Chip
                    label={columnData.data_type}
                    sx={{
                      backgroundColor: "#eaf2fa",
                      color: "#3F49A7",
                      fontFamily: "monospace",
                      fontSize: 12,
                      fontWeight: 700,
                      height: 28,
                      "& .MuiChip-label": {
                        px: 1.5,
                      },
                    }}
                  />
                </Box>
              )}

              {/* Description */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <DescriptionIcon
                      sx={{
                        fontSize: 18,
                        color: "#3F49A7",
                        opacity: 0.8,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#666",
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Description
                    </Typography>
                  </Box>
                  {!isEditMode && (
                    <IconButton
                      size="small"
                      onClick={handleEdit}
                      sx={{
                        color: "#3F49A7",
                        "&:hover": {
                          backgroundColor: "rgba(63, 73, 167, 0.08)",
                        },
                        padding: 0.5,
                      }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>

                {isEditMode ? (
                  <Box>
                    <TextField
                      multiline
                      rows={6}
                      fullWidth
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      placeholder="Enter column description..."
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#fff",
                          fontSize: 14,
                          "& fieldset": {
                            borderColor: "#e9ecef",
                          },
                          "&:hover fieldset": {
                            borderColor: "#3F49A7",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#3F49A7",
                          },
                        },
                      }}
                    />
                    {updateError && (
                      <Alert
                        severity="error"
                        sx={{
                          mt: 1.5,
                          fontSize: 12,
                        }}
                      >
                        {updateError.message || "Failed to update description"}
                      </Alert>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        mt: 2,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleCancel}
                        startIcon={<CancelIcon />}
                        sx={{
                          color: "#666",
                          borderColor: "#e0e0e0",
                          textTransform: "none",
                          fontSize: 13,
                          "&:hover": {
                            borderColor: "#999",
                            backgroundColor: "rgba(0,0,0,0.02)",
                          },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleSave}
                        disabled={updating || !editedDescription.trim()}
                        startIcon={
                          updating ? (
                            <CircularProgress size={14} sx={{ color: "#fff" }} />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        sx={{
                          backgroundColor: "#3F49A7",
                          textTransform: "none",
                          fontSize: 13,
                          "&:hover": {
                            backgroundColor: "#2e357a",
                          },
                        }}
                      >
                        {updating ? "Saving..." : "Save"}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <>
                    {updateSuccess && (
                      <Alert
                        severity="success"
                        sx={{
                          mb: 1.5,
                          fontSize: 12,
                        }}
                      >
                        Description updated successfully!
                      </Alert>
                    )}
                    {columnData.description ? (
                      <Box
                        sx={{
                          p: 2.5,
                          backgroundColor: "#f8f9fa",
                          borderRadius: 2,
                          border: "1px solid #e9ecef",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#333",
                            fontSize: 14,
                            lineHeight: 1.7,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {columnData.description}
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          p: 2.5,
                          backgroundColor: "#f8f9fa",
                          borderRadius: 2,
                          border: "1px solid #e9ecef",
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#999",
                            fontSize: 13,
                            fontStyle: "italic",
                          }}
                        >
                          No description available for this column.
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Box>
          )}

          {/* No Data State */}
          {!loading && !error && !columnData && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <DescriptionIcon
                    sx={{
                      fontSize: 18,
                      color: "#3F49A7",
                      opacity: 0.8,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#666",
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Description
                  </Typography>
                </Box>
                {!isEditMode && (
                  <IconButton
                    size="small"
                    onClick={handleEdit}
                    sx={{
                      color: "#3F49A7",
                      "&:hover": {
                        backgroundColor: "rgba(63, 73, 167, 0.08)",
                      },
                      padding: 0.5,
                    }}
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>

              {isEditMode ? (
                <Box>
                  <TextField
                    multiline
                    rows={6}
                    fullWidth
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    placeholder="Enter column description..."
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#fff",
                        fontSize: 14,
                        "& fieldset": {
                          borderColor: "#e9ecef",
                        },
                        "&:hover fieldset": {
                          borderColor: "#3F49A7",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3F49A7",
                        },
                      },
                    }}
                  />
                  {updateError && (
                    <Alert
                      severity="error"
                      sx={{
                        mt: 1.5,
                        fontSize: 12,
                      }}
                    >
                      {updateError.message || "Failed to update description"}
                    </Alert>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      mt: 2,
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCancel}
                      startIcon={<CancelIcon />}
                      sx={{
                        color: "#666",
                        borderColor: "#e0e0e0",
                        textTransform: "none",
                        fontSize: 13,
                        "&:hover": {
                          borderColor: "#999",
                          backgroundColor: "rgba(0,0,0,0.02)",
                        },
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSave}
                      disabled={updating || !editedDescription.trim()}
                      startIcon={
                        updating ? (
                          <CircularProgress size={14} sx={{ color: "#fff" }} />
                        ) : (
                          <SaveIcon />
                        )
                      }
                      sx={{
                        backgroundColor: "#3F49A7",
                        textTransform: "none",
                        fontSize: 13,
                        "&:hover": {
                          backgroundColor: "#2e357a",
                        },
                      }}
                    >
                      {updating ? "Saving..." : "Save"}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 2.5,
                    backgroundColor: "#f8f9fa",
                    borderRadius: 2,
                    border: "1px solid #e9ecef",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#999",
                      fontSize: 13,
                      fontStyle: "italic",
                      mb: 1,
                    }}
                  >
                    No description found for this column.
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleEdit}
                    startIcon={<EditIcon />}
                    sx={{
                      color: "#3F49A7",
                      borderColor: "#3F49A7",
                      textTransform: "none",
                      fontSize: 12,
                      mt: 1,
                      "&:hover": {
                        borderColor: "#2e357a",
                        backgroundColor: "rgba(63, 73, 167, 0.08)",
                      },
                    }}
                  >
                    Add Description
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ColumnDescriptionModal;
